import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Scale } from "lucide-react";
import { toast } from "sonner";

export default function FirmLogin() {
  const { slug } = useParams();
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/firm/branding/${slug}`)
      .then(({ data }) => setBrand(data))
      .catch(() => setBrand(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !agreed) { toast.error("Please accept the terms to continue."); return; }
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome!");
        navigate("/dashboard");
      } else {
        await register(form.name, form.email, form.password, brand?.firm_name || "", agreed, slug, "attorney_firm");
        toast.success("Welcome!");
        navigate("/onboarding/business");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed. Check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  }
  if (!brand) {
    return (
      <div className="min-h-screen grid place-items-center px-6 text-center">
        <div>
          <h1 className="font-serif text-3xl mb-2">Firm not found</h1>
          <p className="text-muted-foreground mb-6">This login link is invalid or has been removed.</p>
          <Link to="/login"><Button className="rounded-sm">Go to standard login</Button></Link>
        </div>
      </div>
    );
  }

  const disp = brand.brand_display || (brand.logo ? "both" : "name");
  const showLogo = (disp === "logo" || disp === "both");
  const showName = (disp === "name" || disp === "both" || !brand.logo);
  const accent = brand.accent_color || null;
  // Pick readable text color for a custom accent (dark text on light accents).
  const accentText = (() => {
    if (!accent) return undefined;
    const h = accent.replace("#", "");
    const f = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    const r = parseInt(f.slice(0, 2), 16), g = parseInt(f.slice(2, 4), 16), b = parseInt(f.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return undefined;
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.6 ? "#0f172a" : "#ffffff";
  })();

  return (
    <div className="min-h-screen grid place-items-center px-6 py-12 bg-secondary/30" data-testid="firm-login-page">
      <div className="w-full max-w-sm">
        {/* Centered firm brand — logo, name, or both per the firm's choice */}
        <div className="flex flex-col items-center text-center mb-8" data-testid="firm-login-brand">
          {showLogo && brand.logo && (
            <img src={brand.logo} alt={brand.firm_name} data-testid="firm-login-logo" className="max-h-24 max-w-[220px] object-contain mb-4" />
          )}
          {showLogo && !brand.logo && (
            <span className="grid place-items-center w-14 h-14 bg-primary text-primary-foreground mb-4"><Scale size={26} /></span>
          )}
          <h1 className={`font-serif text-3xl tracking-tight ${showName ? "" : "sr-only"}`} data-testid="firm-login-name">{brand.firm_name}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {mode === "login" ? "Sign in to your firm workspace" : "Create your firm account"}
          </p>
        </div>

        <div className="bg-white border-2 border-black p-6 sm:p-8">
          <Button onClick={loginWithGoogle} variant="outline" data-testid="firm-google-login-btn"
            className="w-full rounded-sm h-11 gap-3 mb-6 hover:bg-secondary">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">or</span><span className="h-px flex-1 bg-border" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs">Full name</Label>
                <Input id="name" data-testid="firm-register-name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-11 mt-1" placeholder="Jane M. Doe" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs">Email</Label>
              <Input id="email" type="email" data-testid="firm-login-email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm h-11 mt-1" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs">Password</Label>
              <Input id="password" type="password" data-testid="firm-login-password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm h-11 mt-1" placeholder="••••••••" />
            </div>
            {mode === "register" && (
              <label className="flex items-start gap-3 text-xs text-muted-foreground cursor-pointer">
                <Checkbox data-testid="firm-agree-terms" checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" />
                <span>
                  I agree to the{" "}
                  <Link to="/legal/terms" target="_blank" className="text-accent hover:underline">Terms</Link>,{" "}
                  <Link to="/legal/privacy" target="_blank" className="text-accent hover:underline">Privacy Policy</Link> &amp;{" "}
                  <Link to="/legal/eula" target="_blank" className="text-accent hover:underline">EULA</Link>.
                </span>
              </label>
            )}
            <Button type="submit" disabled={busy} data-testid="firm-login-submit"
              style={accent ? { backgroundColor: accent, color: accentText } : undefined}
              className="w-full rounded-sm h-11 bg-primary hover:opacity-90 transition-opacity">
              {busy ? "Please wait…" : mode === "login" ? "Log in" : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            {mode === "login" ? "New to the firm? " : "Already have an account? "}
            <button data-testid="firm-toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}
              style={accent ? { color: accent } : undefined}
              className="text-accent font-medium hover:underline">
              {mode === "login" ? "Create an account" : "Log in"}
            </button>
          </p>
        </div>
        <p className="text-center text-[0.7rem] text-muted-foreground mt-6">Powered by NC Legal Forms</p>
      </div>
    </div>
  );
}
